import classNames from 'classnames';
import React, { FC, memo, useCallback, useLayoutEffect, useMemo } from 'react';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { Button } from 'shared/ui/Button';
import { ReactComponent as CheckMark } from 'shared/assets/icons/CheckMark.svg';
import {
  Box,
  useGetBoxRentalPriceString,
  useGetBoxDiscountedPriceString,
  parseDiscountData,
  useGetBoxRentalPriceStringWithVat,
} from 'entities/Box';
import { roundNumber } from 'shared/utils/helpers/roundNumber';
import { useScreenBreakpoints } from 'shared/utils/hooks/useScreenBreakpoints';
import { getLocalizedString } from 'shared/utils/helpers/JSONLocalization';
import { useScrollToElement } from 'shared/utils/hooks/useScrollToElement';
import { useAppSelector } from 'app/config/storeConfig/hooks';
import { getWarehouseInfo } from 'pages/BookingPage';
import { trimPriceFrequency } from '../utils/helpers/trimPriceFrequency';
import { DiscountLabel } from './DiscountLabel';
import { ReactComponent as InfoIcon } from 'shared/assets/icons/InfoIcon.svg';
// import { InvoiceFrequencyType } from 'entities/Invoice';
import { getGlobalSettings, useGetCurrencySymbol } from 'app/appState';
import { Popup } from 'shared/ui/Popup';
import { WarehouseDiscount } from 'entities/Warehouse';
import { FIRST_MONTH_DISCOUNT_REGEX } from 'shared/utils/regex';
import { InvoiceFrequencyType } from 'entities/Invoice';
import { UserAccountType } from 'entities/User';
import SizeCodeDefaultImage from 'shared/assets/images/SizeCodeDefaultImage.png';
import { UnitType } from 'entities/Contract';

interface OfficeRowProps {
  box: Box;
  selectedBoxId: string | undefined;
  hasRentDiscounts: boolean;
  hasGeneralDiscounts: boolean;
  onSelectBox: (box: Box) => void;
  onBookBox: (box: Box) => void;
  accountType?: Nullable<UserAccountType>;
}

export const OfficeRow: FC<OfficeRowProps> = memo((props) => {
  const { box, selectedBoxId, onSelectBox, onBookBox, accountType } = props;

  const { t } = useAppTranslation('booking');
  const { scrollToElement, scrollRef } = useScrollToElement('center', 500);
  const { isDesktop, isMobile } = useScreenBreakpoints();
  const warehouseInfo = useAppSelector(getWarehouseInfo);
  const globalSettings = useAppSelector(getGlobalSettings);

  const showPriceWithVat = globalSettings?.showPriceWithVat;
  const discountedPrice = useGetBoxDiscountedPriceString({
    warehouseInfo,
    boxInfo: box,
    showPriceWithVat,
    accountType,
    unitType: box.unitType,
  });

  const currencySymbol = useGetCurrencySymbol();

  const discountedPriceText = discountedPrice?.split(currencySymbol);

  const firstMonthDiscount = useMemo((): WarehouseDiscount | undefined => {
    if (!warehouseInfo?.warehouseDiscounts) {
      return;
    }

    const sortedDiscounts = [...warehouseInfo.warehouseDiscounts].sort((a, b) => a.priority - b.priority);

    const actualDiscount = sortedDiscounts?.find((discount) => {
      const hasUnitSizes = Boolean(discount.unitSizes?.length);

      const includesFirstMonth = discount.discounts.split(',').some((discount) => FIRST_MONTH_DISCOUNT_REGEX.test(discount.trim()));

      if (hasUnitSizes) {
        const unitSizes = discount.unitSizes.split(',').map((size) => Number(size));

        const includesSize = unitSizes?.includes(box.sizeCode.square);

        return includesFirstMonth && includesSize;
      }

      return includesFirstMonth;
    });

    return actualDiscount;
  }, [box.sizeCode.square, warehouseInfo?.warehouseDiscounts]);

  const parsedDiscount = useMemo(() => {
    return parseDiscountData(firstMonthDiscount);
  }, [firstMonthDiscount]);

  useLayoutEffect(() => {
    selectedBoxId === box.boxId && scrollToElement();
  }, [box.boxId, scrollToElement, selectedBoxId]);

  const discount = box.priceFactor < 1 ? 100 - box.priceFactor * 100 : null;
  const isSelected = selectedBoxId === box.boxId;

  const rentalBoxPriceWithVat = useGetBoxRentalPriceStringWithVat(box, InvoiceFrequencyType.MONTH, warehouseInfo, accountType);

  const rentalBoxPrice = useGetBoxRentalPriceString(box);

  const rentalPrice = showPriceWithVat ? rentalBoxPriceWithVat : rentalBoxPrice;

  // const weekRate = useGetBoxRentalPriceString(box, InvoiceFrequencyType.WEEK);

  const handleSelectBox = useCallback(
    (e: React.MouseEvent<HTMLDivElement>): void => {
      e.stopPropagation();
      e.preventDefault();

      onSelectBox(box);
    },
    [box, onSelectBox],
  );

  const handleBookBox = useCallback((): void => {
    onBookBox(box);
  }, [box, onBookBox]);

  const imageUrl = box.imageUrl || box.sizeCode.imageUrl || SizeCodeDefaultImage;

  return (
    <>
      <div className="desktop:hidden">{discount && <DiscountLabel discount={discount} />}</div>
      <div ref={scrollRef} className="p-4 desktop:p-0" onClick={handleSelectBox}>
        <div className={classNames('p-4 desktop:p-3 rounded-lg ease-linear duration-100', { 'bg-accentLight': isSelected })}>
          <div
            className={classNames('flex space-x-3 justify-between cursor-pointer desktop:px-3 wideDesktop:px-6', {
              'flex-col': !isDesktop,
              'items-center': isDesktop,
            })}
          >
            {box.sizeCode.imageUrl && (
              <div className={classNames('basis-1/13 shrink-0', { 'mb-3': isMobile })}>
                <img
                  className={classNames('object-contain', { 'w-[100%]': isMobile, 'w-[123px]': isDesktop })}
                  src={imageUrl}
                  alt="Size image"
                />
              </div>
            )}
            <div className="flex flex-col desktop:basis-[85%]">
              <div className="flex text-lg items-center gap-2">
                {t('Office')} {box.name}
                {box.timeToOpenLockMS !== null && <span className="text-xs text-accent">({t('External box')})</span>}
              </div>

              <div className="flex text-xs text-primaryLight items-center py-2">
                {t('Ideal for small businesses and freelancers. Unfurnished, customizable')}
              </div>

              <div className="flex gap-1">
                <div className="flex flex-col">
                  {box.floor && (
                    <div className="flex items-center space-x-1.5">
                      <CheckMark className="shrink-0 stroke-accent" />
                      <span className="text-xs font-normal text-primaryLight">{getLocalizedString(box.floor.name)}</span>
                    </div>
                  )}
                  <div className="flex items-center space-x-1.5">
                    <CheckMark className="shrink-0 stroke-1 stroke-accent" />
                    <span className="text-xs font-normal text-primaryLight"> {t('24/7 Access')} </span>
                  </div>
                </div>
                <div className="flex flex-col">
                  <div className="flex items-center space-x-1.5">
                    <CheckMark className="shrink-0 stroke-1 stroke-accent" />
                    <span className="text-xs font-normal text-primaryLight"> {t('Central location')} </span>
                  </div>
                  <div className="flex items-center space-x-1.5">
                    <CheckMark className="shrink-0 stroke-1 stroke-accent" />
                    <span className="text-xs font-normal text-primaryLight"> {t('Incl. heating, electricity, cleaning & internet')} </span>
                  </div>
                </div>
              </div>
            </div>

            <div className={classNames('flex flex-col items-end mt-3 desktop:items-center desktop:basis-[40%] desktop:mt-0')}>
              <div className="flex flex-col text-base wideDesktop:items-end wideDesktop:flex-row text-primary font-semibold desktop:text-md wideDesktop:text-xl">
                {discountedPrice ? (
                  <>
                    <div className="flex items-center gap-1 text-base desktop:text-md wideDesktop:text-xl font-semibold ">
                      {isDesktop
                        ? `${discountedPriceText?.[0]} ${currencySymbol}/${t('Month')}`
                        : trimPriceFrequency(`${discountedPriceText?.[0]} ${currencySymbol}/${t('Month')}`)}
                    </div>
                    <div className="flex items-center">
                      <span className="text-xs desktop:text-sm desktop:p-1">{discountedPriceText?.[1]}</span>

                      <Popup description={<div className="max-w-[310px] desktop:max-w-[600px]">{parsedDiscount?.description}</div>}>
                        <InfoIcon width={20} height={20} className="[&>path]:stroke-primary cursor-pointer flex-shrink-0 mb-2" />
                      </Popup>
                    </div>
                  </>
                ) : !isDesktop ? (
                  trimPriceFrequency(rentalPrice)
                ) : (
                  rentalPrice
                )}
              </div>
              {discountedPrice && (
                <div
                  className={classNames('text-error text-sm desktop:text-base wideDesktop:text-lg', { 'line-through': discountedPrice })}
                >
                  {!isDesktop ? trimPriceFrequency(rentalPrice) : rentalPrice}
                </div>
              )}
              {/* {weekRate && (
                <div className="text-primary">{!isDesktop ? trimPriceFrequency(weekRate) : `${t('Regular price')} ${weekRate}`}</div>
              )} */}
            </div>
            <div className="hidden desktop:flex desktop:justify-center desktop:basis-[30%]">
              <Button containerClassName="shrink-0" onClick={handleBookBox}>
                {t('Reserve now')}
              </Button>
            </div>
          </div>
          <Button containerClassName="mx-2 mt-3 desktop:hidden" onClick={handleBookBox}>
            {t('Reserve now')}
          </Button>
        </div>
      </div>
    </>
  );
});
